import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import classes from "./ContactAdmin.module.css";
import API from "../../utils/api";
import RenderToast from "../../components/RenderToast";
import useDebounce from "../../hooks/useDebounce";
import ShowContactDetailModal from "../../Modal/ShowContactDetailModal";
import DeleteContactDetailModal from "../../Modal/DeleteContactDetailModal";
import moment from "moment";
import { FiDownload, FiEye, FiTrash2, FiRefreshCw } from "react-icons/fi";
import LoaderComp from "../../components/LoaderComp";

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const formatPakistanDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";
    const pakistanDate = new Date(
      date.toLocaleString("en-US", { timeZone: "Asia/Karachi" })
    );
    const day = String(pakistanDate.getDate()).padStart(2, "0");
    const month = String(pakistanDate.getMonth() + 1).padStart(2, "0");
    const year = pakistanDate.getFullYear();
    let hours = pakistanDate.getHours();
    const minutes = String(pakistanDate.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, "0");
    return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
  } catch {
    return "N/A";
  }
};

export default function ContactAdmin() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [viewMessage, setViewMessage] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const [exporting, setExporting] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const [user, setUser] = useState(readUser());

  useEffect(() => {
    const handleStorage = () => setUser(readUser());
    const handleUserUpdate = () => setUser(readUser());
    window.addEventListener("storage", handleStorage);
    window.addEventListener("userUpdated", handleUserUpdate);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("userUpdated", handleUserUpdate);
    };
  }, []);

  const can = (permission) => user && Number(user[permission]) === 1;
  const canAccessContact = can("contact_access");
  const canRefreshButton = can("contact_refresh_button");
  const canExportButton = can("contact_export_button");
  const canDeleteSelectedButton = can("contact_delete_selected_button");
  const canViewAction = can("contact_view_action");
  const canDeleteAction = can("contact_delete_action");

  const token = localStorage.getItem("authToken") || "";

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const formData = new URLSearchParams();
      formData.append("action", "fetch");
      const response = await fetch(`${API.base}/contact_messages.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to load messages");
      setMessages(Array.isArray(data) ? data : []);
      setSelected(new Set());
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const filtered = useMemo(() => {
    if (!debouncedSearch.trim()) return messages;
    const term = debouncedSearch.toLowerCase();
    return messages.filter(
      (msg) =>
        (msg.name || "").toLowerCase().includes(term) ||
        (msg.email || "").toLowerCase().includes(term) ||
        (msg.sku || "").toLowerCase().includes(term)
    );
  }, [messages, debouncedSearch]);

  const toggleSelect = (id) => {
    if (!canDeleteSelectedButton) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!canDeleteSelectedButton) return;
    setSelected(
      selected.size === filtered.length
        ? new Set()
        : new Set(filtered.map((m) => m.id))
    );
  };

  const truncate = (text, length = 10) =>
    text?.length > length ? text.slice(0, length) + "..." : text;

  const markAsRead = async (message) => {
    try {
      const formData = new URLSearchParams();
      formData.append("action", "mark_read");
      formData.append("id", message.id);
      const response = await fetch(`${API.base}/contact_messages.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to mark read");
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, is_read: 1 } : m))
      );
      setViewMessage((prev) => ({ ...prev, is_read: 1 }));
      RenderToast({ type: "success", message: "Message marked as read" });
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
    }
  };

  const handleExport = async () => {
    if (!canExportButton) return;
    if (!token) {
      return RenderToast({ type: "error", message: "Authentication required" });
    }
    setExporting(true);
    try {
      const body = new URLSearchParams();
      body.append("action", "export_csv");
      const response = await fetch(`${API.base}/contact_messages.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
      });
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || contentType.includes("application/json")) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || "Failed to export messages");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `contact_messages_${moment().format(
        "YYYYMMDD_HHmmss"
      )}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      RenderToast({ type: "success", message: "Contact messages exported" });
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
    } finally {
      setExporting(false);
    }
  };

  if (!canAccessContact) {
    return (
      <DashboardLayout>
        <div className={classes.page}>
          <div className={classes.emptyState}>
            You do not have permission to access contact messages.
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className={classes.page}>
        <div className={classes.topBar}>
          <div>
            <p className={classes.kicker}>Inbox</p>
            <h2 className={classes.title}>Contact Messages</h2>
            <p className={classes.subtitle}>
              Review enquiries submitted through the contact form.
            </p>
          </div>
          <div className={classes.actions}>
            <input
              type="search"
              placeholder="Search by name, email, or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              name="search"
            />
            {canRefreshButton && (
              <button className={classes.refreshBtn} onClick={fetchMessages}>
                <FiRefreshCw /> Refresh
              </button>
            )}
            {canExportButton && (
              <button
                className={classes.exportBtn}
                type="button"
                onClick={handleExport}
                disabled={exporting}
              >
                <FiDownload />
                {exporting ? "Exporting..." : "Export CSV"}
              </button>
            )}
            {canDeleteSelectedButton && (
              <button
                className={classes.dangerBtn}
                disabled={selected.size === 0}
                onClick={() => {
                  setDeleteMessage({
                    ids: Array.from(selected),
                    names: [],
                  });
                }}
              >
                Delete Selected ({selected.size})
              </button>
            )}
          </div>
        </div>

        <div className={classes.card}>
          {loading ? (
            <LoaderComp />
          ) : filtered.length === 0 ? (
            <div className={classes.emptyState}>No contact messages found.</div>
          ) : (
            <div className={classes.tableWrapper}>
              <table>
                <thead>
                  <tr>
                    {canDeleteSelectedButton && (
                      <th>
                        <input
                          type="checkbox"
                          checked={
                            selected.size === filtered.length &&
                            filtered.length > 0
                          }
                          onChange={toggleSelectAll}
                        />
                      </th>
                    )}
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Message</th>
                    <th>Created At</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((msg) => (
                    <tr key={msg.id}>
                      {canDeleteSelectedButton && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(msg.id)}
                            onChange={() => toggleSelect(msg.id)}
                          />
                        </td>
                      )}
                      <td>
                        <code className={classes.sku}>{msg.sku || "—"}</code>
                      </td>
                      <td>{msg.name}</td>
                      <td>{msg.email}</td>
                      <td>{msg.phone || "N/A"}</td>
                      <td>{truncate(msg.address) || "N/A"}</td>
                      <td>{truncate(msg.message)}</td>
                      <td>{formatPakistanDateTime(msg.created_at)}</td>
                      <td>
                        <span
                          className={
                            msg.is_read ? classes.statusRead : classes.statusNew
                          }
                        >
                          {msg.is_read ? "Read" : "Unread"}
                        </span>
                      </td>
                      <td>
                        <div className={classes.rowActions}>
                          {canViewAction && (
                            <button
                              className={classes.iconBtn}
                              onClick={() => setViewMessage(msg)}
                              title="View"
                            >
                              <FiEye />
                            </button>
                          )}
                          {canDeleteAction && (
                            <button
                              className={classes.iconBtnDanger}
                              onClick={() => {
                                const msgToDelete = filtered.find(
                                  (m) => m.id === msg.id
                                );
                                setDeleteMessage({
                                  ids: [msg.id],
                                  names: [msgToDelete?.name || "—"],
                                });
                              }}
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                          {!canViewAction && !canDeleteAction && (
                            <span className={classes.noActions}>—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {viewMessage && (
        <ShowContactDetailModal
          message={viewMessage}
          onClose={() => setViewMessage(null)}
          onMarkRead={() => markAsRead(viewMessage)}
        />
      )}

      {deleteMessage && (
        <DeleteContactDetailModal
          messages={deleteMessage}
          onClose={() => setDeleteMessage(null)}
          onDeleted={fetchMessages}
        />
      )}
    </DashboardLayout>
  );
}
